import { useState, useEffect, useRef } from "react";
import { FaWhatsapp } from "react-icons/fa";
import CountUp from "react-countup";
import { motion, AnimatePresence } from "framer-motion";

// const URL = "http://localhost:5000/";
const URL = "https://skp-backend-619387713761.asia-south1.run.app/"
const HomePage = () => {
    const [collections, setCollections] = useState([]);
    const [gallery, setGallery] = useState([]);

    const countryCodes = [
        { code: "+1", label: "USA" },
        { code: "+7", label: "RUS" },
        { code: "+20", label: "EGY" },
        { code: "+27", label: "ZAF" },
        { code: "+30", label: "GRC" },
        { code: "+31", label: "NLD" },
        { code: "+32", label: "BEL" },
        { code: "+33", label: "FRA" },
        { code: "+34", label: "ESP" },
        { code: "+36", label: "HUN" },
        { code: "+39", label: "ITA" },
        { code: "+40", label: "ROU" },
        { code: "+44", label: "GBR" },
        { code: "+45", label: "DNK" },
        { code: "+46", label: "SWE" },
        { code: "+47", label: "NOR" },
        { code: "+48", label: "POL" },
        { code: "+49", label: "DEU" },
        { code: "+52", label: "MEX" },
        { code: "+53", label: "CUB" },
        { code: "+54", label: "ARG" },
        { code: "+55", label: "BRA" },
        { code: "+56", label: "CHL" },
        { code: "+57", label: "COL" },
        { code: "+58", label: "VEN" },
        { code: "+60", label: "MYS" },
        { code: "+61", label: "AUS" },
        { code: "+62", label: "IDN" },
        { code: "+63", label: "PHL" },
        { code: "+64", label: "NZL" },
        { code: "+65", label: "SGP" },
        { code: "+66", label: "THA" },
        { code: "+81", label: "JPN" },
        { code: "+82", label: "KOR" },
        { code: "+84", label: "VNM" },
        { code: "+86", label: "CHN" },
        { code: "+90", label: "TUR" },
        { code: "+91", label: "IND" },
        { code: "+92", label: "PAK" },
        { code: "+93", label: "AFG" },
        { code: "+94", label: "LKA" },
        { code: "+95", label: "MMR" },
        { code: "+98", label: "IRN" },
    ];


    // State to track the selected collection for modal
    const [selectedCollection, setSelectedCollection] = useState(null);
    const [fullscreenImage, setFullscreenImage] = useState(null);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    // Fetch data from Flask backend
    useEffect(() => {
        fetch(`${URL}collections`) // change to your backend URL
            .then((res) => res.json())
            .then((data) => setCollections(data))
            .catch((err) => console.error("Error fetching collections:", err));

        fetch(`${URL}gallery`)
            .then((res) => res.json())
            .then((data) => setGallery(data))
            .catch((err) => console.error("Error fetching gallery:", err));
    }, []);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        countryCode: "+91",
        phone: "",
        message: "",
    });
    const [formStatus, setFormStatus] = useState(null);

    const navLinks = [
        { href: "/", label: "Home" },
        { href: "#overview", label: "Overview" },
        { href: "#collections", label: "Collections" },
        { href: "#gallery", label: "Gallery" },
        { href: "#contact", label: "Contact" },
    ];

    // Mobile menu state
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const firstLinkRef = useRef(null);
    const mobilePanelRef = useRef(null);

    // Focus the first link when opening
    useEffect(() => {
        if (mobileMenuOpen) {
            // small timeout to allow animation start then focus
            setTimeout(() => firstLinkRef.current?.focus(), 150);
        }
    }, [mobileMenuOpen]);

    // Close on ESC
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") setMobileMenuOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "phone") {
            // allow only digits, remove anything else
            const digitsOnly = value.replace(/\D/g, "");
            setFormData((prev) => ({ ...prev, [name]: digitsOnly }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setLoading(true);

        let req = {
            name: formData.name,
            email: formData.email,
            phone: formData.countryCode + " " + formData.phone,
            message: formData.message
        }

        try {
            const response = await fetch(`${URL}contact`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(req),
            });

            // const data = await response.json();

            if (response.ok) {
                setFormStatus("Thank you for contacting us! We will get back to you soon.");
                setFormData({
                    name: "",
                    email: "",
                    countryCode: "+91",
                    phone: "",
                    message: "",
                });
                setErrors({});
            } else {
                setFormStatus("Something went wrong, please try again.");
            }
            setLoading(false);
        } catch (error) {
            setFormStatus("Failed to submit form. Please check your connection and try again.");
            setLoading(false);
        }
    };

    // Open modal with collection details
    const openCollectionModal = (collection) => {
        setSelectedCollection(collection);
    };

    // Close modal
    const closeModal = () => {
        setSelectedCollection(null);
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.name.trim()) newErrors.name = "Name is required";

        if (!formData.email.trim()) newErrors.email = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(formData.email))
            newErrors.email = "Email is invalid";

        if (!formData.phone.trim()) newErrors.phone = "Phone is required";
        else if (!/^\d+$/.test(formData.phone))
            newErrors.phone = "Phone must be numeric";

        if (!formData.message.trim()) newErrors.message = "Message is required";

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const features = [
        {
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mb-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 4H7a2 2 0 01-2-2V6a2 2 0 012-2h8l6 6v8a2 2 0 01-2 2z" />
                </svg>
            ),
            title: "Expertised Knowledge",
            text: "Our team has deep expertise in the industry.",
        },
        {
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mb-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
            ),
            title: "Best in Quality",
            text: "We maintain top standards to deliver superior products.",
        },
        {
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mb-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
                    <circle cx="12" cy="12" r="6" stroke="currentColor" strokeWidth="2" fill="none" />
                    <circle cx="12" cy="12" r="2" fill="currentColor" />
                </svg>
            ),
            title: "Precise and Accurate",
            text: "We ensure every detail is carefully checked and verified.",
        },
        {
            icon: (
                <svg
                    className="w-12 h-12 mb-4"
                    fill="#16a34a"
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 256 173"
                    xmlSpace="preserve"
                >
                    <path d="M128.253,56.864c15.186,0,27.432-12.247,27.432-27.432S143.536,2,128.253,2 c-15.186,0-27.432,12.247-27.432,27.432C100.918,44.716,113.165,56.864,128.253,56.864z M64.571,136.32h-49.28 c-15.969,0-16.851-24.395,0.294-24.395H58.3l24.493-36.054c7.25-9.895,15.48-14.598,27.138-14.598h36.544 c11.659,0,19.888,4.311,27.138,14.598l24.591,36.054h43.01c17.243,0,16.165,24.395,0.588,24.395h-49.28 c-3.919,0-8.622-1.372-11.365-5.584l-18.811-26.844l-0.098,67.209H94.844l-0.098-67.209l-18.811,26.844 C73.192,134.85,68.49,136.32,64.571,136.32z"></path>
                    <path d="M30.957,95.543C46.939,95.543,60,82.568,60,66.5S47.025,37.457,30.957,37.457S2,50.518,2,66.586 C2,82.568,14.975,95.543,30.957,95.543z M29.353,68.963c-5.327-1.862-8.65-4.554-8.65-9.337c0-4.411,2.921-7.877,8.278-8.965v-4.783 h4.01v4.554c3.322,0,5.728,0.687,7.504,1.633l-1.633,5.728c-1.318-0.544-3.466-1.461-6.416-1.461s-4.554,1.461-4.554,2.921 c0,2.005,1.862,2.778,5.872,4.411c5.471,2.005,8.02,4.783,8.02,9.337c0,4.554-2.778,8.02-8.65,9.337v4.554h-4.01v-4.411 c-3.466,0-6.96-0.917-8.65-1.862l1.461-5.872c1.862,0.917,4.783,2.005,7.877,2.005c3.322,0,4.955-1.461,4.955-3.466 S33.191,70.453,29.353,68.963z"></path>
                    <path d="M243.225,48.936l2.553-4.05l-6.041-3.808l-2.509,3.979c-2.886-1.409-6.032-2.365-9.344-2.776v-3.799h4.395v-7.141h-15.656 v7.141h4.12v3.772c-14.811,1.718-26.346,14.336-26.346,29.6c0,16.433,13.369,29.802,29.802,29.802S254,88.289,254,71.856 C254,62.65,249.804,54.407,243.225,48.936z M224.198,94.522c-12.498,0-22.666-10.168-22.666-22.666S211.7,49.19,224.198,49.19 s22.666,10.168,22.666,22.666S236.696,94.522,224.198,94.522z M242.716,71.719h-18.403V53.316 C234.456,53.364,242.607,61.59,242.716,71.719z"></path>
                </svg>
            ),
            title: "Low Cost",
            text: "Affordable pricing without compromising quality.",
        },
        {
            icon: (
                <svg
                    fill="#16a34a" // green-600 color hex
                    height="48px"
                    width="48px"
                    viewBox="0 0 265.583 265.583"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <g>
                        <g>
                            <g>
                                <path d="M264.28,148.489l-30.366-45.017c-1.413-2.097-3.779-3.353-6.305-3.353h-26.262
              c-0.905-12.879-11.646-23.089-24.753-23.089h-67.417c-4.525-25.811-27.084-45.494-54.168-45.494C24.677,31.535,0,56.215,0,86.548
              c0,23.838,15.245,44.178,36.496,51.799v39.41c0,13.409,10.685,24.342,23.982,24.799c-0.008,0.279-0.043,0.553-0.043,0.834
              c0,16.904,13.754,30.657,30.66,30.657s30.66-13.754,30.66-30.657c0-0.266-0.033-0.525-0.041-0.791h41.497
              c-0.01,0.264-0.041,0.522-0.041,0.791c0,16.904,13.751,30.657,30.657,30.657c16.906,0,30.662-13.754,30.662-30.657
              c0-0.266-0.035-0.525-0.041-0.791h33.526c4.2,0,7.609-3.406,7.609-7.609v-42.245C265.584,151.23,265.128,149.747,264.28,148.489z
               M15.215,86.553c0-21.943,17.852-39.795,39.793-39.795S94.8,64.61,94.8,86.553s-17.852,39.795-39.793,39.795
              C33.066,126.348,15.215,108.496,15.215,86.553z M91.092,218.835c-8.514,0-15.443-6.926-15.443-15.44
              c0-8.517,6.926-15.445,15.443-15.445c8.517,0,15.443,6.926,15.443,15.445C106.535,211.906,99.609,218.835,91.092,218.835z
               M167.729,187.39h-50.533v-0.004h-0.002c-5.4-8.773-15.065-14.654-26.102-14.654c-11.037,0-20.7,5.881-26.1,14.654h-3.655
              c-5.308,0-9.627-4.319-9.627-9.627v-36.3c1.093,0.063,2.191,0.107,3.3,0.107c28.41,0,51.857-21.649,54.716-49.316h66.869
              c5.306,0,9.625,4.319,9.625,9.627v71.853C178.429,175.729,171.845,180.707,167.729,187.39z M193.828,218.835
              c-8.514,0-15.44-6.926-15.44-15.44c0-8.517,6.924-15.445,15.44-15.445s15.445,6.926,15.445,15.445
              C209.273,211.906,202.342,218.835,193.828,218.835z M219.928,187.386c-4.068-6.607-10.556-11.55-18.23-13.591v-58.459h21.864
              l26.802,39.734v32.316H219.928z"></path>
                                <path d="M71.718,112.305c2.369,0,4.702-1.101,6.186-3.17c2.45-3.414,1.671-8.167-1.745-10.619l-13.543-9.721V64.64
              c0-4.202-3.406-7.609-7.609-7.609s-7.609,3.406-7.609,7.609v27.898c0,0.066,0.018,0.129,0.02,0.195
              c0.013,2.354,1.093,4.669,3.152,6.145l16.716,11.999C68.632,111.841,70.181,112.305,71.718,112.305z"></path>
                            </g>
                        </g>
                    </g>
                </svg>
            ),
            title: "On-Time Delivery",
            text: "We respect your schedule and deliver promptly.",
        }
    ];

    const cardVariants = {
        hidden: { opacity: 0, y: 40, scale: 0.95 },
        visible: (i) => ({
            opacity: 1,
            y: 0,
            scale: 1,
            transition: { duration: 0.6, delay: i * 0.15, ease: "easeOut" }
        }),
    };

    const modalVariants = {
        hidden: { opacity: 0, scale: 0.9 },
        visible: { opacity: 1, scale: 1, transition: { duration: 0.3 } },
        exit: { opacity: 0, scale: 0.9, transition: { duration: 0.2 } },
    };


    const fadeUp = {
        hidden: { opacity: 0, y: 30 },
        visible: (delay = 0) => ({
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, delay, ease: "easeOut" },
        }),
    };

    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev === gallery.length - 1 ? 0 : prev + 1));
        }, 5000); // change slide every 5 seconds
        return () => clearInterval(interval);
    }, [gallery.length]);

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 font-sans">
            {/* Top Contact Bar */}
            <div className="w-full bg-green-500 text-white border-b border-gray-800">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="min-h-[42px] flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-2 sm:gap-6 py-2 text-xs sm:text-sm">

                        {/* Phone */}
                        <a
                            href="tel:+918675713624"
                            className="flex items-center gap-2 text-white hover:text-white transition-colors duration-200"
                        >
                            <svg
                                className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M22 16.92v3a2 2 0 01-2.18 2
                        19.79 19.79 0 01-8.63-3.07
                        19.5 19.5 0 01-6-6
                        19.79 19.79 0 01-3.07-8.67
                        A2 2 0 014.11 2h3a2 2 0 012 1.72
                        12.84 12.84 0 00.7 2.81
                        2 2 0 01-.45 2.11L8.09 9.91
                        a16 16 0 006 6l1.27-1.27
                        a2 2 0 012.11-.45
                        12.84 12.84 0 002.81.7
                        A2 2 0 0122 16.92z"
                                />
                            </svg>

                            <span className="whitespace-nowrap">
                                +91 8675713624
                            </span>
                        </a>

                        {/* Email */}
                        <a
                            href="mailto:info@srikumaranplast.com"
                            className="flex items-center gap-2 text-white hover:text-white transition-colors duration-200"
                        >
                            <svg
                                className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                            >
                                <rect
                                    x="3"
                                    y="5"
                                    width="18"
                                    height="14"
                                    rx="2"
                                />
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M3 7l9 6 9-6"
                                />
                            </svg>

                            <span className="break-all sm:break-normal">
                                srikumaranplast@gmail.com
                            </span>
                        </a>

                    </div>
                </div>
            </div>
            {/* Header */}
            <motion.header
                className="bg-gray-900 text-white shadow-md"
                initial={{ y: -60, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
            >
                <div className="flex justify-between items-center px-6 py-4 w-full">

                    {/* Logo + Name aligned to left edge */}
                    <motion.a
                        href="/"
                        className="flex items-center text-2xl font-bold tracking-wide hover:text-green-500 transition"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1, duration: 0.5 }}
                    >
                        <img
                            src={`${URL}images/favicon.jpg`}
                            alt="Company Logo"
                            className="h-10 mr-2"
                        />
                        <span>Sri Kumaran Plast</span>
                    </motion.a>

                    {/* Desktop Nav */}
                    <nav className="hidden md:flex space-x-8 font-semibold">
                        {navLinks.map(({ href, label }, i) => (
                            <motion.a
                                key={label}
                                href={href}
                                className="hover:text-green-600 transition"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 + i * 0.1, duration: 0.4 }}
                            >
                                {label}
                            </motion.a>
                        ))}
                    </nav>

                    {/* Mobile menu button */}
                    <motion.button
                        onClick={() => setMobileMenuOpen((s) => !s)}
                        className="md:hidden focus:outline-none"
                        aria-controls="mobile-menu"
                        aria-expanded={mobileMenuOpen}
                        aria-label="Toggle menu"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3, duration: 0.4 }}
                    >
                        {mobileMenuOpen ? (
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" className="text-white">
                                <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        ) : (
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" className="text-white">
                                <path d="M3 12h18M3 6h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        )}
                    </motion.button>
                </div>
            </motion.header>


            {/* MOBILE MENU */}
            <div
                id="mobile-menu"
                ref={mobilePanelRef}
                className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out ${mobileMenuOpen ? "max-h-[80vh] opacity-100" : "max-h-0 opacity-0"
                    }`}
                aria-hidden={!mobileMenuOpen}
            >
                <div className="bg-gray-900 text-white px-6 py-6">
                    <nav className="flex flex-col space-y-4">
                        {navLinks.map(({ href, label }, i) => (
                            <a
                                key={label}
                                href={href}
                                ref={i === 0 ? firstLinkRef : undefined}
                                onClick={() => setMobileMenuOpen(false)}
                                className="text-lg font-semibold hover:text-green-500 focus:outline-none focus:ring-2 focus:ring-green-400 px-2 py-1 rounded"
                            >
                                {label}
                            </a>
                        ))}
                    </nav>

                    <div className="mt-6">
                        <a
                            href="#contact"
                            onClick={() => setMobileMenuOpen(false)}
                            className="inline-block w-full text-center bg-green-600 hover:bg-green-700 text-white px-5 py-3 rounded shadow font-semibold transition"
                        >
                            Request Quote
                        </a>
                    </div>
                </div>
            </div>

            {/* Hero Banner */}
            <motion.section
                className="relative min-h-[680px] lg:min-h-[720px] overflow-hidden bg-black text-white"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
            >
                {/* Background Image - Keep/Replace your image here */}
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: `url(${URL}images/cover002.png)`,
                    }}
                />

                {/* Dark gradient - creates the black left side like the reference */}
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/95 via-[42%] to-black/20" />

                {/* Additional dark overlay */}
                <div className="absolute inset-0 bg-black/20" />

                {/* Content */}
                <div className="relative z-10 container mx-auto min-h-[680px] lg:min-h-[720px] px-6 sm:px-8 lg:px-12">
                    <div className="flex min-h-[680px] lg:min-h-[720px] items-center">
                        <div className="w-full lg:w-[55%] xl:w-[52%] py-20 lg:py-24">

                            {/* Welcome Text */}
                            <motion.div
                                className="mb-5 text-sm sm:text-base md:text-lg font-semibold tracking-wide uppercase text-green-500"
                                initial={{ x: -40, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ duration: 0.7, ease: "easeOut" }}
                            >
                                Welcome to Sri Kumaran Plast
                            </motion.div>

                            {/* Main Heading */}
                            <motion.h1
                                className="text-xl sm:text-5xl md:text-6xl lg:text-[4rem] xl:text-[4.3rem] font-extrabold leading-[1.05] tracking-tight mb-7"
                                initial={{ x: -50, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{
                                    duration: 0.8,
                                    delay: 0.1,
                                    ease: "easeOut",
                                }}
                            >
                                Precision Injection Moulding
                                <span className="block mt-2 text-green-500">
                                    Solutions for Every Need
                                </span>
                            </motion.h1>

                            {/* Description */}
                            <motion.p
                                className="max-w-2xl text-base sm:text-lg md:text-xl leading-relaxed text-gray-200 mb-9"
                                initial={{ x: -40, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{
                                    duration: 0.8,
                                    delay: 0.2,
                                    ease: "easeOut",
                                }}
                            >
                                We manufacture high-quality plastic products with
                                advanced technology, strict quality control and a
                                commitment to customer satisfaction.
                            </motion.p>

                            {/* CTA Buttons */}
                            <motion.div
                                className="flex flex-wrap gap-4 mb-14"
                                initial={{ y: 25, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{
                                    duration: 0.7,
                                    delay: 0.3,
                                    ease: "easeOut",
                                }}
                            >
                                {/* Primary Button */}
                                <motion.a
                                    href="#collections"
                                    whileHover={{ scale: 1.04 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="group inline-flex items-center justify-center gap-3 bg-green-600 hover:bg-green-500 text-white px-7 py-4 font-semibold text-sm sm:text-base transition-all duration-300 shadow-lg shadow-green-900/20"
                                >
                                    Explore Products

                                    <span className="text-lg transition-transform duration-300 group-hover:translate-x-1">
                                        →
                                    </span>
                                </motion.a>

                                {/* Secondary Button */}
                                <motion.a
                                    href="#our-infrastructure"
                                    whileHover={{ scale: 1.04 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="group inline-flex items-center justify-center gap-3 border border-gray-400 hover:border-white text-white px-7 py-4 font-semibold text-sm sm:text-base transition-all duration-300 bg-black/20 hover:bg-white/10"
                                >
                                    Our Infrastructure

                                    <span className="text-lg transition-transform duration-300 group-hover:translate-x-1">
                                        →
                                    </span>
                                </motion.a>
                            </motion.div>

                            {/* Feature Highlights */}
                            <motion.div
                                className="grid grid-cols-2 md:grid-cols-4 gap-7 md:gap-5 lg:gap-7"
                                initial={{ y: 30, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{
                                    duration: 0.8,
                                    delay: 0.45,
                                    ease: "easeOut",
                                }}
                            >
                                {/* Experience */}
                                <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0">
                                        <svg
                                            className="w-9 h-9 text-green-500"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M15.5 21a6.5 6.5 0 00-13 0"
                                            />
                                            <circle cx="9" cy="7" r="4" />
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M16 3.5a4 4 0 010 7.5"
                                            />
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M18 13a5.5 5.5 0 014 5.3"
                                            />
                                        </svg>
                                    </div>

                                    <div>
                                        <div className="font-bold text-lg leading-tight">
                                            25+
                                        </div>
                                        <div className="text-xs sm:text-sm text-gray-300 mt-1">
                                            Years Experience
                                        </div>
                                    </div>
                                </div>

                                {/* Quality */}
                                <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0">
                                        <svg
                                            className="w-9 h-9 text-green-500"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M12 3l7 3v5c0 4.7-3 8.7-7 10-4-1.3-7-5.3-7-10V6l7-3z"
                                            />
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M9 12l2 2 4-4"
                                            />
                                        </svg>
                                    </div>

                                    <div>
                                        <div className="font-bold text-sm sm:text-base leading-tight">
                                            High Quality
                                        </div>
                                        <div className="text-xs sm:text-sm text-gray-300 mt-1">
                                            Raw Materials
                                        </div>
                                    </div>
                                </div>

                                {/* Machinery */}
                                <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0">
                                        <svg
                                            className="w-9 h-9 text-green-500"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle cx="12" cy="12" r="3" />
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M19.4 15a1.7 1.7 0 00.3 1.9l.1.1-1.7 1.7-.1-.1a1.7 1.7 0 00-1.9-.3 1.7 1.7 0 00-1 1.6V20h-2.4v-.1a1.7 1.7 0 00-1-1.6 1.7 1.7 0 00-1.9.3l-.1.1L8 17l.1-.1a1.7 1.7 0 00.3-1.9 1.7 1.7 0 00-1.6-1H6v-2.4h.1a1.7 1.7 0 001.6-1 1.7 1.7 0 00-.3-1.9L7.3 8.6 9 7l.1.1a1.7 1.7 0 001.9.3 1.7 1.7 0 001-1.6V5h2.4v.1a1.7 1.7 0 001 1.6 1.7 1.7 0 001.9-.3l.1-.1 1.7 1.7-.1.1a1.7 1.7 0 00-.3 1.9 1.7 1.7 0 001.6 1h.1v2.4h-.1a1.7 1.7 0 00-1.6 1z"
                                            />
                                        </svg>
                                    </div>

                                    <div>
                                        <div className="font-bold text-sm sm:text-base leading-tight">
                                            Advanced
                                        </div>
                                        <div className="text-xs sm:text-sm text-gray-300 mt-1">
                                            Machinery
                                        </div>
                                    </div>
                                </div>

                                {/* Delivery */}
                                <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0">
                                        <svg
                                            className="w-9 h-9 text-green-500"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M3 6h11v11H3z"
                                            />
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M14 10h4l3 3v4h-7z"
                                            />
                                            <circle cx="7" cy="19" r="2" />
                                            <circle cx="18" cy="19" r="2" />
                                        </svg>
                                    </div>

                                    <div>
                                        <div className="font-bold text-sm sm:text-base leading-tight">
                                            Timely
                                        </div>
                                        <div className="text-xs sm:text-sm text-gray-300 mt-1">
                                            Delivery
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </motion.section>


            {/* Overview */}
            <motion.section
                id="overview"
                className="py-20 bg-white"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6 }}
            >
                <div className="container mx-auto px-6 max-w-6xl">
                    <motion.h2
                        className="text-3xl font-bold text-center mb-8"
                        initial={{ y: 30, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        About <span className="text-green-600">Us</span>
                    </motion.h2>

                    <motion.p
                        className="text-center text-gray-700 max-w-3xl mx-auto leading-relaxed text-lg mb-12"
                        initial={{ y: 20, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1, duration: 0.6 }}
                    >
                        Sri Kumaran Plast, established in 1997, is a leading manufacturer specializing in plastic hangers and a wide range of plastic injection-moulded components. With a well-equipped in-house production facility and robust infrastructure, we are committed to delivering products of the highest quality. Currently, we maintain an extensive inventory of over 500 plastic hangers and moulds to meet diverse customer needs.
                    </motion.p>

                    {/* Stats */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
                        {[
                            { value: 100, suffix: "+", label: "Happy Customers" },
                            { value: 500, suffix: "+", label: "Unique Models" },
                            { value: 25, suffix: "+", label: "Expert Employees" },
                        ].map((stat, i) => (
                            <motion.div
                                key={i}
                                className="p-6 bg-gray-50 rounded-lg shadow-lg hover:shadow-green-400 transition"
                                initial={{ scale: 0.8, opacity: 0 }}
                                whileInView={{ scale: 1, opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.2, duration: 0.5, ease: "easeOut" }}
                            >
                                <h3 className="text-4xl font-extrabold text-green-600">
                                    <CountUp
                                        start={0}
                                        end={stat.value}
                                        suffix={stat.suffix}
                                        duration={5}
                                        enableScrollSpy
                                        scrollSpyOnce
                                    />
                                </h3>
                                <p className="mt-2 text-gray-600 font-medium">{stat.label}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </motion.section>


            {/* Why Choose Us Section */}
            <section id="why-choose-us" className="py-20 bg-white">
                <div className="container mx-auto px-6 max-w-6xl text-center">
                    <h2 className="text-3xl font-bold mb-12">
                        Why <span className="text-green-600">Choose Us</span>
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
                        {features.map((feature, i) => (
                            <motion.div
                                key={i}
                                className="flex flex-col items-center p-6 bg-gray-50 rounded-lg shadow-lg hover:shadow-green-300 transition transform hover:-translate-y-2"
                                variants={cardVariants}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                custom={i}
                            >
                                {feature.icon}
                                <h3 className="font-semibold text-lg mb-1">{feature.title}</h3>
                                <p className="text-gray-600 text-sm">{feature.text}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Our Infrastructure Section */}
            <section id="our-infrastructure" className="py-20 bg-gray-50">
                <div className="container mx-auto px-6 max-w-7xl flex flex-col md:flex-row items-center gap-12">
                    {/* Left Content */}
                    <motion.div
                        className="flex-1"
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-3xl font-bold mb-6">
                            Our <span className="text-green-600">Infrastructure</span>
                        </h2>
                        <p className="text-gray-700 mb-6 leading-relaxed">
                            Our company is equiped with machines of leading brands which helps in the accuracy and precision of the products and
                            fitted with all other auxilary equipments.
                        </p>
                        <ul className="space-y-3">
                            {[
                                "In House Tool Room",
                                "Chillers",
                                "Shredders",
                                "Mixers",
                                "Necessary Lab Testing Equipments",
                            ].map((point, i) => (
                                <motion.li
                                    key={i}
                                    className="flex items-start gap-3"
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ delay: i * 0.15, duration: 0.5 }}
                                    viewport={{ once: true }}
                                >
                                    <span className="text-green-600 text-lg">✔</span>
                                    <span className="text-gray-700">{point}</span>
                                </motion.li>
                            ))}
                        </ul>
                    </motion.div>

                    {/* Right Video */}
                    <motion.div
                        className="flex-1 flex justify-center"
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <video
                            src={`${URL}/images/moulding-1.mp4`}// <-- Replace with your video file path
                            autoPlay
                            muted
                            loop
                            playsInline
                            className="rounded-lg shadow-lg w-full max-w-lg border-4 border-green-200"
                        />
                    </motion.div>
                </div>
            </section>

            {/* Collections Section */}
            <section id="collections" className="py-20 bg-gray-50">
                <div className="container mx-auto px-6 max-w-6xl">
                    <h2 className="text-3xl font-bold text-center mb-12">
                        Our <span className="text-green-600">Products</span>
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                        {collections.map((collection, i) => {
                            const isVideo = /\.(mp4|mov|avi|mkv|webm)$/i.test(collection.image);
                            return (
                                <motion.div
                                    key={collection.id}
                                    custom={i}
                                    variants={cardVariants}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true }}
                                    onClick={() => openCollectionModal(collection)}
                                    className="cursor-pointer bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-green-400 transition transform hover:-translate-y-2"
                                >
                                    <div className="h-64 w-full overflow-hidden relative">
                                        {isVideo ? (
                                            <video
                                                src={`${URL}${collection.image}`}
                                                className="w-full h-full object-cover"
                                                muted
                                                autoPlay
                                                loop
                                                playsInline
                                                preload="metadata"
                                            />
                                        ) : (
                                            <motion.img
                                                src={`${URL}${collection.image}`}
                                                alt={collection.title}
                                                className="w-full h-full object-cover"
                                                whileHover={{ scale: 1.05 }}
                                                transition={{ duration: 0.4 }}
                                            />
                                        )}
                                        {/* Watermark in center */}
                                        <div className="absolute inset-0 flex justify-center items-center pointer-events-none select-none">
                                            <span
                                                className="text-white/30 text-4xl font-bold"
                                                style={{
                                                    transform: "rotate(-45deg)",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                © Sri Kumaran Plast
                                            </span>
                                        </div>
                                    </div>
                                    <div className="p-8 flex-1 flex flex-col">
                                        <h3 className="text-xl font-semibold mb-3">{collection.title}</h3>
                                        {/* <p className="text-gray-600 flex-1">{collection.description}</p> */}
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Modal Popup for Collection */}
            <AnimatePresence>
                {selectedCollection && (
                    <>
                        {/* Collection modal */}
                        <motion.div
                            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70"
                            onClick={closeModal}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <motion.div
                                variants={modalVariants}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                                className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto p-6 relative"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <button
                                    onClick={closeModal}
                                    className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 text-2xl font-bold"
                                    aria-label="Close modal"
                                >
                                    &times;
                                </button>

                                <h2 className="text-3xl font-bold mb-4">{selectedCollection.title}</h2>

                                {/* Products List */}
                                <h3 className="text-2xl font-semibold mb-3">Products</h3>
                                {selectedCollection.products.length === 0 ? (
                                    <p>No products available.</p>
                                ) : (
                                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {selectedCollection.products.map((product) => {
                                            const isVideo = /\.(mp4|mov|avi|mkv|webm)$/i.test(product.image);
                                            return (
                                                <motion.li
                                                    key={product.id}
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.4 }}
                                                    className="border rounded p-4 flex items-center gap-4"
                                                >
                                                    {isVideo ? (
                                                        <video
                                                            src={`${URL}${product.image}`}
                                                            className="w-24 h-24 object-cover rounded cursor-pointer"
                                                            muted
                                                            autoPlay
                                                            loop
                                                            playsInline
                                                            preload="metadata"
                                                            onClick={() => setFullscreenImage(`${URL}${product.image}`)}
                                                        />
                                                    ) : (
                                                        <img
                                                            src={`${URL}${product.image}`}
                                                            alt={product.name}
                                                            className="w-24 h-24 object-cover rounded cursor-pointer bg-green-500"
                                                            onClick={() => setFullscreenImage(`${URL}${product.image}`)}
                                                        />
                                                    )}
                                                    <div>
                                                        <h4 className="font-semibold text-lg">{product.name}</h4>
                                                        {/* <p className="text-gray-700">{product.description}</p> */}
                                                    </div>
                                                </motion.li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </motion.div>
                        </motion.div>

                        {/* Fullscreen Media Modal */}
                        <AnimatePresence>
                            {fullscreenImage && (
                                <motion.div
                                    className="fixed inset-0 z-[100] bg-black bg-opacity-90 flex items-center justify-center p-4"
                                    onClick={() => setFullscreenImage(null)}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                >
                                    <button
                                        onClick={() => setFullscreenImage(null)}
                                        className="absolute top-6 right-6 text-white text-3xl font-bold z-10"
                                        aria-label="Close fullscreen media"
                                    >
                                        &times;
                                    </button>

                                    {/\.(mp4|mov|avi|mkv|webm)$/i.test(fullscreenImage) ? (
                                        <motion.video
                                            src={fullscreenImage}
                                            className="w-full h-full object-cover"
                                            muted={false}
                                            autoPlay
                                            controls
                                            loop
                                            playsInline
                                            preload="metadata"
                                            initial={{ scale: 0.9, opacity: 0 }}
                                            animate={{ scale: 1, opacity: 1 }}
                                            exit={{ scale: 0.9, opacity: 0 }}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    ) : (
                                        <motion.img
                                            src={fullscreenImage}
                                            alt="Fullscreen media"
                                            className="max-h-full max-w-full rounded shadow-lg bg-green-500"
                                            initial={{ scale: 0.9, opacity: 0 }}
                                            animate={{ scale: 1, opacity: 1 }}
                                            exit={{ scale: 0.9, opacity: 0 }}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    )}
                                    {/* Watermark in center */}
                                    <div className="absolute inset-0 flex justify-center items-center pointer-events-none select-none">
                                        <span
                                            className="text-white/30 text-4xl font-bold"
                                            style={{
                                                transform: "rotate(-45deg)",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            © Sri Kumaran Plast
                                        </span>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </>
                )}
            </AnimatePresence>

            {/* Gallery Slideshow */}
            <section id="gallery" className="py-20 bg-white">
                <div className="container mx-auto px-6 max-w-6xl text-center">
                    <h2 className="text-3xl font-bold mb-12">
                        <span className="text-green-600">Gallery</span>
                    </h2>

                    {gallery.length > 0 && (
                        <div className="relative w-full max-w-4xl mx-auto">
                            <motion.div
                                key={currentIndex}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.8 }}
                                className="rounded-lg overflow-hidden shadow-lg border border-green-100 bg-black flex items-center justify-center"
                            >
                                {/\.(mp4|mov|avi|mkv|webm)$/i.test(gallery[currentIndex]) ? (
                                    <video
                                        src={`${URL}${gallery[currentIndex]}`}
                                        className="max-w-full max-h-[500px] object-contain"
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        preload="metadata"
                                    />
                                ) : (
                                    <motion.img
                                        src={`${URL}${gallery[currentIndex]}`}
                                        alt={`Slide ${currentIndex + 1}`}
                                        className="max-w-full max-h-[500px] object-contain"
                                        loading="lazy"
                                        initial={{ scale: 1.05 }}
                                        animate={{ scale: 1 }}
                                        transition={{ duration: 0.8 }}
                                    />
                                )}
                                {/* Watermark in center */}
                                <div className="absolute inset-0 flex justify-center items-center pointer-events-none select-none">
                                    <span
                                        className="text-white/30 text-4xl font-bold"
                                        style={{
                                            transform: "rotate(-45deg)",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        © Sri Kumaran Plast
                                    </span>
                                </div>
                            </motion.div>

                            {/* Prev Button */}
                            <button
                                onClick={() =>
                                    setCurrentIndex((prev) => (prev === 0 ? gallery.length - 1 : prev - 1))
                                }
                                className="absolute top-1/2 left-4 transform -translate-y-1/2 bg-white/70 hover:bg-white text-green-600 rounded-full p-3 shadow-md transition"
                            >
                                ❮
                            </button>

                            {/* Next Button */}
                            <button
                                onClick={() =>
                                    setCurrentIndex((prev) => (prev === gallery.length - 1 ? 0 : prev + 1))
                                }
                                className="absolute top-1/2 right-4 transform -translate-y-1/2 bg-white/70 hover:bg-white text-green-600 rounded-full p-3 shadow-md transition"
                            >
                                ❯
                            </button>

                            {/* Dots Indicator */}
                            <div className="flex justify-center mt-4 gap-2">
                                {gallery.map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setCurrentIndex(i)}
                                        className={`w-3 h-3 rounded-full transition ${currentIndex === i ? "bg-green-600" : "bg-gray-300"
                                            }`}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* Contact */}
            <motion.section
                id="contact"
                className="py-20 bg-gray-50"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
            >
                <div className="container mx-auto px-6 max-w-6xl">
                    <motion.h2
                        className="text-3xl font-bold text-center mb-10"
                        variants={fadeUp}
                        custom={0}
                    >
                        Contact <span className="text-green-600">Us</span>
                    </motion.h2>

                    <div className="flex flex-col md:flex-row gap-12">
                        {/* Contact Form */}
                        <motion.form
                            onSubmit={handleSubmit}
                            noValidate
                            className="bg-white rounded-lg shadow-lg p-10 flex-1"
                            variants={fadeUp}
                            custom={0.1}
                        >
                            {/* Name */}
                            <motion.div className="mb-6" variants={fadeUp} custom={0.15}>
                                <label htmlFor="name" className="block mb-2 font-semibold">
                                    Name
                                </label>
                                <input
                                    required
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className={`w-full border rounded px-4 py-3 focus:outline-none focus:ring-2 ${errors.name
                                        ? "border-red-500 focus:ring-red-500"
                                        : "border-gray-300 focus:ring-green-500"
                                        }`}
                                    placeholder="Your full name"
                                />
                                {errors.name && <p className="text-red-600 mt-1">{errors.name}</p>}
                            </motion.div>

                            {/* Email */}
                            <motion.div className="mb-6" variants={fadeUp} custom={0.2}>
                                <label htmlFor="email" className="block mb-2 font-semibold">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    required
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className={`w-full border rounded px-4 py-3 focus:outline-none focus:ring-2 ${errors.email
                                        ? "border-red-500 focus:ring-red-500"
                                        : "border-gray-300 focus:ring-green-500"
                                        }`}
                                    placeholder="you@example.com"
                                />
                                {errors.email && <p className="text-red-600 mt-1">{errors.email}</p>}
                            </motion.div>

                            {/* Phone */}
                            <motion.div className="mb-6" variants={fadeUp} custom={0.25}>
                                <label htmlFor="phone" className="block mb-2 font-semibold">
                                    Phone
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    <select
                                        name="countryCode"
                                        value={formData.countryCode}
                                        onChange={handleChange}
                                        className="border border-gray-300 rounded px-3 py-3 focus:outline-none focus:ring-2 focus:ring-green-500 w-fit flex-shrink-0"
                                    >
                                        {countryCodes.map(({ code, label }) => (
                                            <option key={code} value={code}>
                                                ({code})
                                            </option>
                                        ))}
                                    </select>
                                    <input
                                        required
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className={`flex-grow min-w-[180px] border rounded px-4 py-3 focus:outline-none focus:ring-2 ${errors.phone
                                            ? "border-red-500 focus:ring-red-500"
                                            : "border-gray-300 focus:ring-green-500"
                                            }`}
                                        placeholder="Enter phone number"
                                    />
                                </div>
                                {errors.phone && <p className="text-red-600 mt-1">{errors.phone}</p>}
                            </motion.div>

                            {/* Message */}
                            <motion.div className="mb-6" variants={fadeUp} custom={0.3}>
                                <label htmlFor="message" className="block mb-2 font-semibold">
                                    Message
                                </label>
                                <textarea
                                    required
                                    id="message"
                                    name="message"
                                    rows="5"
                                    value={formData.message}
                                    onChange={handleChange}
                                    className={`w-full border rounded px-4 py-3 focus:outline-none focus:ring-2 ${errors.message
                                        ? "border-red-500 focus:ring-red-500"
                                        : "border-gray-300 focus:ring-green-500"
                                        }`}
                                    placeholder="Write your message here"
                                />
                                {errors.message && (
                                    <p className="text-red-600 mt-1">{errors.message}</p>
                                )}
                            </motion.div>

                            {/* Submit Button */}
                            <motion.button
                                type="submit"
                                disabled={loading}
                                className={`w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded shadow transition flex items-center justify-center ${loading ? "cursor-not-allowed opacity-70" : ""
                                    }`}
                                whileTap={{ scale: 0.96 }}
                            >
                                {loading ? (
                                    <svg
                                        className="animate-spin h-5 w-5 mr-3 text-white"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                        />
                                    </svg>
                                ) : (
                                    "Send Message"
                                )}
                            </motion.button>

                            {formStatus && (
                                <p className="mt-4 text-center text-green-600 font-semibold">
                                    {formStatus}
                                </p>
                            )}
                        </motion.form>

                        {/* Contact Info + Map */}
                        <motion.div
                            className="flex-1 bg-white rounded-lg shadow-lg p-10 flex flex-col"
                            variants={fadeUp}
                            custom={0.35}
                        >
                            <motion.h3
                                className="text-2xl font-semibold mb-6"
                                variants={fadeUp}
                                custom={0.4}
                            >
                                Reach Us
                            </motion.h3>
                            <motion.div
                                className="mb-6 space-y-4 text-gray-700"
                                variants={fadeUp}
                                custom={0.45}
                            >
                                <div>
                                    <h4 className="font-semibold">Address</h4>
                                    <p>78, Kaithea Millathe Street</p>
                                    <p>Punjai Puliampatti, Erode(Dt) - 638459</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold">Phone</h4>
                                    <p>+91 8675713624</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold">Email</h4>
                                    <p>srikumaranplast@gmail.com</p>
                                </div>
                            </motion.div>

                            <motion.div
                                className="flex-grow rounded overflow-hidden shadow-inner"
                                initial={{ scale: 0.95, opacity: 0 }}
                                whileInView={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.6, delay: 0.5, ease: "easeOut" }}
                                viewport={{ once: true }}
                            >
                                <iframe
                                    title="Google Map"
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3911.7776154047297!2d77.1609834745818!3d11.350947448334859!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba8e1917dc3dd85%3A0xc63ea103141d8559!2sSri%20Kumaran%20Plast!5e0!3m2!1sen!2sin!4v1754733409411!5m2!1sen!2sin"
                                    width="100%"
                                    height="250"
                                    className="border-0 w-full h-full"
                                    allowFullScreen=""
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                ></iframe>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </motion.section>

            {/* Footer */}
            <footer className="bg-gray-900 text-gray-400 py-10">
                <div className="container mx-auto px-6 max-w-6xl text-center">
                    <div className="mb-8">
                        <img
                            src={`${URL}images/logo.png`}
                            alt="Company Logo"
                            className="mx-auto h-24 mb-4"
                        />
                        <p className="max-w-md mx-auto text-sm leading-relaxed text-gray-400">
                            © 2025 Manufacturer. All rights reserved.<br />Delivering excellence in manufacturing solutions.
                        </p>
                    </div>

                    {/* <Link
                        to="/admin"
                        className="text-sm font-medium text-green-500 cursor-pointer hover:text-green-400 transition"
                    >
                        Login as Admin
                    </Link> */}
                </div>
            </footer>

            {/* whatsapp icon */}
            <a
                href="https://wa.me/918675713624" // Replace with your WhatsApp number
                target="_blank"
                rel="noopener noreferrer"
                className="fixed bottom-6 right-6 bg-green-500 text-white p-4 rounded-full shadow-lg hover:bg-green-600 transition-all duration-300 flex items-center justify-center"
            >
                <FaWhatsapp size={28} />
            </a>
        </div>
    );
};

export default HomePage;
